import { Clock } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface TimePickerProps {
  /** Time value in "HH:mm" 24h format */
  value?: string;
  /** Callback when time changes — returns "HH:mm" 24h format */
  onChange: (time: string) => void;
  /** Field label */
  label?: string;
  /** Error message */
  error?: string;
  /** Hint text below input */
  hint?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Disable the input */
  disabled?: boolean;
  /** Required field */
  required?: boolean;
  /** Custom class name */
  className?: string;
  /** Minute step (1, 5, 15, etc.) */
  minuteStep?: number;
  /** Compact mode for inline/table use */
  compact?: boolean;
}

/** Parse "HH:mm" into { hour12, minute, period } */
function parse24(value?: string): { hour12: number; minute: number; period: 'AM' | 'PM' } {
  if (!value || !/^\d{2}:\d{2}$/.test(value)) {
    return { hour12: 12, minute: 0, period: 'AM' };
  }
  const [h, m] = value.split(':').map(Number);
  const period: 'AM' | 'PM' = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return { hour12, minute: m, period };
}

/** Convert { hour12, minute, period } back to "HH:mm" */
function to24(hour12: number, minute: number, period: 'AM' | 'PM'): string {
  let h = hour12 % 12;
  if (period === 'PM') h += 12;
  return `${h.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
}

/** Format for display: "hh:mm AM/PM" */
function formatDisplay(value?: string): string {
  if (!value || !/^\d{2}:\d{2}$/.test(value)) return '';
  const { hour12, minute, period } = parse24(value);
  return `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;
}

const TimePicker: React.FC<TimePickerProps> = ({
  value,
  onChange,
  label,
  error,
  hint,
  placeholder = 'Select time',
  disabled = false,
  required = false,
  className = '',
  minuteStep = 1,
  compact = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hourListRef = useRef<HTMLDivElement>(null);
  const minuteListRef = useRef<HTMLDivElement>(null);

  const { hour12, minute, period } = parse24(value);

  // Generate minute options based on step
  const minuteOptions: number[] = [];
  for (let i = 0; i < 60; i += minuteStep) {
    minuteOptions.push(i);
  }

  // Position the dropdown
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownHeight = 280;
    const openBelow = spaceBelow >= dropdownHeight;

    setDropdownStyle({
      position: 'fixed',
      left: rect.left,
      width: Math.max(rect.width, compact ? 220 : 260),
      zIndex: 9999,
      ...(openBelow
        ? { top: rect.bottom + 4 }
        : { bottom: window.innerHeight - rect.top + 4 }),
    });
  }, [compact]);

  // Open handler
  const handleOpen = () => {
    if (disabled) return;
    updatePosition();
    setIsOpen(true);
  };

  // Scroll to selected values when opening
  useEffect(() => {
    if (!isOpen) return;
    requestAnimationFrame(() => {
      scrollToSelected(hourListRef.current, hour12);
      scrollToSelected(minuteListRef.current, minute);
    });
  }, [isOpen, hour12, minute]);

  // Click outside
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        triggerRef.current?.contains(e.target as Node) ||
        dropdownRef.current?.contains(e.target as Node)
      )
        return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  const selectHour = (h: number) => {
    onChange(to24(h, minute, period));
  };

  const selectMinute = (m: number) => {
    onChange(to24(hour12, m, period));
  };

  const selectPeriod = (p: 'AM' | 'PM') => {
    onChange(to24(hour12, minute, p));
  };

  // Sizing
  const paddingClasses = compact ? 'px-3 py-1.5' : 'px-4 py-3';
  const textClasses = compact ? 'text-sm' : 'text-base leading-[1.75em]';

  return (
    <div className={`w-full font-['Inter',sans-serif] ${className}`}>
      {/* Label */}
      {label && !compact && (
        <label className="block text-base font-medium text-[#1F1F1F] mb-2 leading-[1.75em]">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Trigger */}
      <div ref={triggerRef} className="relative">
        <div
          onClick={handleOpen}
          className={`flex items-center w-full ${paddingClasses} border rounded-lg transition-all duration-200 cursor-pointer ${textClasses} font-normal ${
            error
              ? 'border-red-300 focus-within:ring-2 focus-within:ring-red-500 focus-within:border-red-500'
              : disabled
                ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                : 'border-gray-300 hover:border-[var(--brand-primary)] focus-within:ring-2 focus-within:ring-[var(--brand-primary)] focus-within:border-[var(--brand-primary)] focus-within:ring-opacity-20'
          } ${disabled ? 'bg-gray-50 text-[#6E7077]' : 'bg-white text-[#1F1F1F]'}`}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleOpen();
            }
          }}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <span className={`flex-1 ${!value ? 'text-[#6E7077]' : ''}`}>
            {value ? formatDisplay(value) : compact ? '' : placeholder}
          </span>
          <Clock className={`${compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} flex-shrink-0 ml-2 ${disabled ? 'text-gray-300' : 'text-gray-500'}`} />
        </div>
      </div>

      {/* Dropdown via portal */}
      {isOpen &&
        createPortal(
          <div ref={dropdownRef} style={dropdownStyle}>
            <div className="bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
              {/* Column headers */}
              <div className="grid grid-cols-3 border-b border-gray-100 text-xs font-medium text-gray-500 text-center">
                <div className="py-2">Hour</div>
                <div className="py-2 border-x border-gray-100">Minute</div>
                <div className="py-2">AM/PM</div>
              </div>

              {/* Scrollable columns */}
              <div className="grid grid-cols-3" style={{ height: 220 }}>
                {/* Hours */}
                <div ref={hourListRef} className="overflow-y-auto scrollbar-thin">
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(h => (
                    <button
                      key={h}
                      type="button"
                      onClick={() => selectHour(h)}
                      className={`w-full py-2 text-sm text-center transition-colors ${
                        h === hour12
                          ? 'bg-[var(--brand-primary)] text-white font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                      data-value={h}
                    >
                      {h}
                    </button>
                  ))}
                </div>

                {/* Minutes */}
                <div
                  ref={minuteListRef}
                  className="overflow-y-auto border-x border-gray-100 scrollbar-thin"
                >
                  {minuteOptions.map(m => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => selectMinute(m)}
                      className={`w-full py-2 text-sm text-center transition-colors ${
                        m === minute
                          ? 'bg-[var(--brand-primary)] text-white font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                      data-value={m}
                    >
                      {m.toString().padStart(2, '0')}
                    </button>
                  ))}
                </div>

                {/* AM / PM */}
                <div className="flex flex-col">
                  {(['AM', 'PM'] as const).map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => selectPeriod(p)}
                      className={`flex-1 text-sm text-center transition-colors ${
                        p === period
                          ? 'bg-[var(--brand-primary)] text-white font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Error */}
      {error && <p className="mt-2 text-sm text-red-600 font-normal leading-[1.75em]">{error}</p>}
      {/* Hint */}
      {hint && !error && (
        <p className="mt-2 text-sm text-[#6E7077] font-normal leading-[1.75em]">{hint}</p>
      )}
    </div>
  );
};

/** Scroll list to the element with matching data-value */
function scrollToSelected(container: HTMLDivElement | null, val: number) {
  if (!container) return;
  const el = container.querySelector(`[data-value="${val}"]`) as HTMLElement | null;
  if (el) {
    el.scrollIntoView({ block: 'center', behavior: 'instant' });
  }
}

TimePicker.displayName = 'TimePicker';

export default TimePicker;
